import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, ArrowLeft, Loader2, Dumbbell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, pollAiJob } from "@/lib/api";
import { tr, AI_LANG_NAME, muscleName } from "@/data/i18n";
import { MUSCLE_LIST } from "@/data/exercises";
import { WorkoutView } from "@/components/WorkoutView";

const GOALS = ["Forza Massimale", "Ipertrofia / Massa", "Resistenza & Calisthenics", "Ricomposizione Corporea", "Dimagrimento"];
const SPLITS = ["Total Body", "Push / Pull / Legs", "Upper / Lower", "Gruppi Muscolari Specifici", "Monofrequenza"];
const EQUIP = ["Macchinari da Palestra & Bilancieri", "Corpo Libero & Calisthenics", "Ibrido (Macchine + Corpo Libero)"];
const LEVELS = ["Principiante", "Intermedio", "Avanzato"];

export default function AiGenerator() {
  const { lang, user, subscription } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ goal: GOALS[1], split_type: SPLITS[0], equipment: EQUIP[0], level: "Principiante", days_per_week: 3, target_muscles: [], notes: "" });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`;
  };

  const toggleMuscle = (m) => setForm((f) => ({
    ...f, target_muscles: f.target_muscles.includes(m) ? f.target_muscles.filter((x) => x !== m) : [...f.target_muscles, m],
  }));

  const generate = async () => {
    if (!user) { toast.error(tr(lang, "login_required")); return; }
    if (!subscription?.has_access) { toast.error(tr(lang, "need_sub")); navigate("/pricing"); return; }
    setLoading(true);
    try {
      const { data } = await api.generate({ ...form, days_per_week: Number(form.days_per_week), lang: AI_LANG_NAME[lang] });
      const result = await pollAiJob(data.job_id);
      setPlan(result);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Errore generazione");
    }
    setLoading(false);
  };

  if (plan) {
    return (
      <div>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 pt-6">
          <button onClick={() => setPlan(null)} className="inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white" data-testid="back-to-form-btn">
            <ArrowLeft size={16} /> {tr(lang, "back")}
          </button>
        </div>
        <WorkoutView plan={plan} setPlan={setPlan} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-10">
      <button onClick={() => navigate("/")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white">
        <ArrowLeft size={16} /> {tr(lang, "back")}
      </button>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="mb-8 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500 red-glow"><Sparkles className="text-white" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold uppercase text-white">{tr(lang, "mode_ai_title")}</h1>
            <p className="text-sm text-zinc-500">{tr(lang, "mode_ai_desc")}</p>
          </div>
        </div>

        <div className="space-y-5 rounded-3xl border border-zinc-800 bg-zinc-900/50 p-6 sm:p-8">
          <Field label={tr(lang, "goal")}>
            <Select value={form.goal} onChange={(v) => setForm({ ...form, goal: v })} options={GOALS} testid="workout-goal-select" />
          </Field>
          <Field label={tr(lang, "split")}>
            <Select value={form.split_type} onChange={(v) => setForm({ ...form, split_type: v })} options={SPLITS} testid="workout-split-select" />
          </Field>
          <Field label={tr(lang, "equipment")}>
            <Select value={form.equipment} onChange={(v) => setForm({ ...form, equipment: v })} options={EQUIP} testid="workout-equipment-select" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={tr(lang, "level")}>
              <Select value={form.level} onChange={(v) => setForm({ ...form, level: v })} options={LEVELS} testid="workout-level-select" />
            </Field>
            <Field label={tr(lang, "days")}>
              <Select value={String(form.days_per_week)} onChange={(v) => setForm({ ...form, days_per_week: v })} options={["1", "2", "3", "4", "5", "6"]} testid="workout-days-select" />
            </Field>
          </div>
          <Field label={tr(lang, "muscles")}>
            <div className="flex flex-wrap gap-2">
              {MUSCLE_LIST.map((m) => (
                <button key={m} onClick={() => toggleMuscle(m)} data-testid={`muscle-chip-${m}`}
                        className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-all ${
                          form.target_muscles.includes(m) ? "border-red-500 bg-red-500/20 text-red-300" : "border-zinc-700 text-zinc-400 hover:border-red-500/40"}`}>
                  {muscleName(m, lang)}
                </button>
              ))}
            </div>
          </Field>
          <Field label={tr(lang, "notes")}>
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="workout-notes-input"
                      className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm text-white outline-none focus:border-red-500" />
          </Field>

          {user ? (
            <button onClick={generate} disabled={loading} data-testid="generate-ai-plan-btn"
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-red-500 py-4 font-bold text-white transition-all hover:bg-red-600 red-glow disabled:opacity-60">
              {loading ? <><Loader2 className="animate-spin" size={18} /> {tr(lang, "generating")}</> : <><Sparkles size={18} /> {tr(lang, "generate")}</>}
            </button>
          ) : (
            <button onClick={login} className="flex w-full items-center justify-center gap-2 rounded-full bg-red-500 py-4 font-bold text-white hover:bg-red-600">
              <Dumbbell size={18} /> {tr(lang, "login")}
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-zinc-400">{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
            className="w-full appearance-none rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm text-white outline-none focus:border-red-500">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
